import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

import { apiClient, type RequestAuthContext } from '@/lib/api/client';
import type { CopyJob } from '@/lib/api/contracts';

interface CopyJobsContextValue {
  /** Active (queued/running) copy jobs from the server. */
  activeJobs: CopyJob[];
  /** True while the first fetch is in progress. */
  isLoading: boolean;
}

const CopyJobsContext = createContext<CopyJobsContextValue | null>(null);

export function useCopyJobs(): CopyJobsContextValue {
  const ctx = useContext(CopyJobsContext);
  if (!ctx) throw new Error('useCopyJobs must be used within CopyJobsProvider');
  return ctx;
}

interface CopyJobsProviderProps {
  auth: RequestAuthContext;
  children: ReactNode;
}

export function CopyJobsProvider({ auth, children }: CopyJobsProviderProps) {
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<Map<string, string>>(new Map());

  // Poll only active jobs — completed jobs are handled by the notification center.
  const activeQuery = useQuery({
    queryKey: ['copy-jobs-active'],
    queryFn: () => apiClient.listCopyJobs(true, auth),
    refetchInterval: (query) => (query.state.data?.length ? 3000 : 30_000),
  });

  const activeJobs: CopyJob[] = activeQuery.data ?? [];

  // When a job disappears from the active list (finished), invalidate dataset queries.
  useEffect(() => {
    const currentIds = new Set(activeJobs.map((j) => j.job_id));
    const datasetIds = new Set<string>();

    for (const [jobId, prevStatus] of prevStatusRef.current) {
      if (!currentIds.has(jobId) && (prevStatus === 'queued' || prevStatus === 'running')) {
        // Job was active before, now gone — it finished.
        // We don't know the dataset_id from here, so invalidate all dataset-objects queries.
        datasetIds.add('*');
      }
    }

    // Rebuild the prev map from current active jobs
    prevStatusRef.current = new Map(activeJobs.map((j) => [j.job_id, j.status]));

    if (datasetIds.size > 0) {
      queryClient.invalidateQueries({ queryKey: ['dataset-objects'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  }, [activeJobs, queryClient]);

  return (
    <CopyJobsContext.Provider value={{ activeJobs, isLoading: activeQuery.isLoading }}>
      {children}
    </CopyJobsContext.Provider>
  );
}
